import { ethereumApi } from '@yozh-io/1inch-widget-api-client';

import { ApproveApi, SwapApi } from '../api';
import { OneInchApproveTxParams } from './types';

export interface FetchQuoteParams {
  quoteInfo: ethereumApi.ExchangeControllerGetQuoteRequest;
  chainId: number | undefined;
}

export interface FetchSwapParams {
  swapInfo: ethereumApi.ExchangeControllerGetSwapRequest;
  chainId: number | undefined;
}

/**
 * @async
 * @function
 * @description Asynchronously retrieves a 1inch exchange quote for a specified trade.
 * @param {FetchQuoteParams} params - The parameters for the quote trade to be executed.
 * @returns {Promise<ethereumApi.QuoteResponseDto>} - The response containing the quote data.
 * @throws {Error} - Throws an error if the API call fails.
 */
export const fetchOneInchQuote = async (params: FetchQuoteParams): Promise<ethereumApi.QuoteResponseDto> => {
  try {
    return (await SwapApi(params.chainId).exchangeControllerGetQuoteRaw(params.quoteInfo)).raw.json();
  } catch (error) {
    if (error.request) {
      throw new Error('Network request failed');
    } else if (error.json) {
      const e = await error.json();
      if (e.description) {
        const { description } = e;
        throw new Error(description.replace(/^./, description[0].toUpperCase()));
      }
    }
    throw new Error(error.message);
  }
};

/**
 * @async
 * @function
 * @description Asynchronously retrieves a 1inch exchange swap for a specified trade.
 * @param {FetchSwapParams} params - The parameters for the swap trade to be executed.
 * @returns {Promise<ethereumApi.SwapResponseDto>} - The response containing the swap data.
 * @throws {Error} - Throws an error if the API call fails.
 */

export const fetchOneInchSwap = async (params: FetchSwapParams): Promise<ethereumApi.SwapResponseDto> => {
  try {
    return (await SwapApi(params.chainId).exchangeControllerGetSwapRaw(params.swapInfo)).raw.json();
  } catch (error) {
    if (error.request) {
      throw new Error('Network request failed');
    } else if (error.json) {
      const e = await error.json();
      if (e.description) {
        const { description } = e;
        throw new Error(description.replace(/^./, description[0].toUpperCase()));
      }
    }
    throw new Error(error.message);
  }
};

/**
 * @async
 * @function
 * @description Asynchronously retrieves the spender address for the 1inch exchange.
 * @param {number} chainId - ChainId for which to retrieve the spender address.
 * @returns {Promise<string>} - The spender address for the 1inch exchange on the specified chainId.
 * @throws {Error} - Throws an error if the API call fails.
 */
export const fetchOneInchSpender = async (chainId: number): Promise<string> => {
  try {
    return (await (await ApproveApi(chainId).chainApproveControllerGetSpenderRaw()).raw.json()).address;
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * @async
 * @function
 * @description Asynchronously retrieves the data needed to execute an approval transaction on the 1inch exchange.
 * @param {OneInchApproveTxParams} params - The parameters needed to make the approval transaction.
 * @returns {Promise<ethereumApi.ApproveCalldataResponseDto>} - The response containing the data needed to execute the approval transaction.
 * @throws {Error} - Throws an error if the API call fails.
 */
export const fetchOneInchApproveTx = async (
  params: OneInchApproveTxParams
): Promise<ethereumApi.ApproveCalldataResponseDto> => {
  try {
    return (await ApproveApi(params.chainId).chainApproveControllerGetCallDataRaw(params)).raw.json();
  } catch (error) {
    throw new Error(error.message);
  }
};
